import { CardContent, CardShell } from '@/components/cards/common';
import { useFilterContext } from '@/context';
import { DateRange } from '@/types';
import NoDataMessage from '@/components/states/NoDataMessage';
import type { UniqueListenersResponse } from '@/types/api/unique-listeners';
import { getDateString } from '@/utils/dateUtils/dateFormatUtils';
import { useUniqueListenersQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import { PlatformIcon } from '@/components/PlatformIcon';
import { PodcastPlayersTooltipContent } from './PodcastPlayersTooltipContent';
import { Tooltip, Icon } from '@acast-tech/decibel';
import { CircleInfoLightGlyph } from '@acast-tech/decibel-glyphs';

type DataType = UniqueListenersResponse;

const UniqueListenersCard = ({ className }: { className?: string }) => {
  const { filters } = useFilterContext();

  const { queryResult } = useFetchQuery(useUniqueListenersQuery);

  return (
    <CardShell className={className} headerClassName="mb-0">
      <CardContent
        {...queryResult}
        renderData={(data: DataType) => (
          <Data
            data={data}
            selectedDateRange={filters.dateRange}
            selectedShow={filters.selectedShows[0]}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedShow,
  selectedDateRange,
}: {
  data?: DataType;
  selectedShow: string | undefined;
  selectedDateRange: DateRange;
}) => {
  if (!data || selectedShow === undefined) {
    return <NoDataMessage dataType="unique listeners data" />;
  }
  const { to } = selectedDateRange;
  const from = new Date(to);
  from.setDate(from.getDate() - 7);

  const toDateString = getDateString(to);
  const fromDateString = getDateString(from.toISOString());

  const label = 'Unique listeners';
  return (
    <div className="flex flex-col gap-2" data-testid="unique-listeners-card-data">
      <div className="flex flex-row items-center justify-between flex-wrap gap-1 min-h-6">
        <span className="text-textMedGrey">
          {label}
          <Tooltip
            title={
              <div className="flex flex-col gap-2">
                <p className="font-medium">7 days total unique listeners</p>
                <p>
                  The total number of individual people who listened to your podcast in the last 7
                  days of the period you selected.
                </p>
                <p>
                  Includes <strong>RSS</strong> listeners on all podcast apps and websites. This
                  data <u>doesn't</u> currently include HLS data (e.g. if you are uploading video to
                  Apple Podcasts) but this is coming soon.
                </p>
              </div>
            }
            placement="bottom-end"
          >
            <Icon glyph={CircleInfoLightGlyph} />
          </Tooltip>
        </span>
        {data.top_players.length > 0 && (
          <span className="flex flex-row items-center gap-1 text-sm text-textMedGrey">
            <span className="mr-1 text-textMedGrey text-xs font-light">Podcast Players</span>
            {data.top_players.map((appName: string) => (
              <span key={appName} className="flex flex-row items-center gap-0.5">
                <PlatformIcon name={appName} />
              </span>
            ))}
            <div className="flex sm:flex-row gap-x-6">
              <PodcastPlayersTooltipContent />
            </div>
          </span>
        )}
      </div>
      <span className="text-textDarkGrey text-2xl md:text-[24px] font-[700] font-telegraf">
        {(data.unique_listeners ?? 0).toLocaleString()}
      </span>
      <span className="text-textMedGrey text-xs font-normal font-inter mt-2">
        7 days | {fromDateString} - {toDateString}
      </span>
    </div>
  );
};

export default UniqueListenersCard;
